import React from 'react';
import { useSubscription } from '@apollo/client';
import { gql } from '@apollo/client';
import { useContext } from 'react';
import { MessageManager } from 'context';

// Suscripciones para pagos de proveedores
const SUB_PAGO_PROVEEDOR_CREADO = gql`
  subscription PagoProveedorCreado {
    pagoProveedorCreado {
      id
      idRemito
      filePathFactura
      estado
      observacion
      pagado
      createdAt
      updatedAt
      remito {
        id
        proveedor {
          razonSocial
          nombre
          apellido
        }
      }
    }
  }
`;

const SUB_PAGO_PROVEEDOR_MODIFICADO = gql`
  subscription PagoProveedorModificado {
    pagoProveedorModificado {
      id
      idRemito
      filePathFactura
      estado
      observacion
      pagado
      createdAt
      updatedAt
      remito {
        id
        proveedor {
          razonSocial
          nombre
          apellido
        }
      }
    }
  }
`;

const SUB_PAGO_PROVEEDOR_ELIMINADO = gql`
  subscription PagoProveedorEliminado {
    pagoProveedorEliminado {
      id
      idRemito
    }
  }
`;

const SUB_PAGO_PROVEEDOR_ARCHIVO_ELIMINADO = gql`
  subscription PagoProveedorArchivoEliminado {
    pagoProveedorArchivoEliminado {
      id
      idRemito
      filePathFactura
      estado
      remito {
        id
        proveedor {
          razonSocial
          nombre
          apellido
        }
      }
    }
  }
`;

/**
 * Componente para manejar las suscripciones de pagos de proveedores
 * y mostrar notificaciones en tiempo real
 * @param {Function} onUpdate - Callback que se ejecuta cuando hay cambios
 * @param {boolean} showNotifications - Si se deben mostrar notificaciones
 */
function PagoProveedorSubscriptions({ onUpdate, showNotifications = true }) {
  const { handleSnackbar } = useContext(MessageManager);

  const getProveedorName = (remito) => {
    const proveedor = remito?.proveedor;
    if (!proveedor) return 'Proveedor desconocido';
    
    return proveedor.razonSocial || 
           `${proveedor.nombre || ''} ${proveedor.apellido || ''}`.trim() ||
           'Proveedor sin nombre';
  };

  // Suscripción para pagos creados
  useSubscription(SUB_PAGO_PROVEEDOR_CREADO, {
    onData: ({ data }) => {
      const pagoProveedor = data?.data?.pagoProveedorCreado;
      if (pagoProveedor) {
        const proveedorName = getProveedorName(pagoProveedor.remito);
        
        if (showNotifications) {
          handleSnackbar(
            `Nuevo pago creado para ${proveedorName} (Remito #${pagoProveedor.idRemito})`,
            'success'
          );
        }
        
        if (onUpdate) {
          onUpdate('created', pagoProveedor);
        }
      }
    },
    onError: (error) => {
      console.error('Error en suscripción de pagos creados:', error);
    }
  });

  // Suscripción para pagos modificados
  useSubscription(SUB_PAGO_PROVEEDOR_MODIFICADO, {
    onData: ({ data }) => {
      const pagoProveedor = data?.data?.pagoProveedorModificado;
      if (pagoProveedor) {
        const proveedorName = getProveedorName(pagoProveedor.remito);
        
        if (showNotifications) {
          handleSnackbar(
            `Pago actualizado para ${proveedorName} - Estado: ${pagoProveedor.estado}`,
            'info'
          );
        }
        
        if (onUpdate) {
          onUpdate('modified', pagoProveedor);
        }
      }
    },
    onError: (error) => {
      console.error('Error en suscripción de pagos modificados:', error);
    }
  });

  // Suscripción para pagos eliminados
  useSubscription(SUB_PAGO_PROVEEDOR_ELIMINADO, {
    onData: ({ data }) => {
      const pagoEliminado = data?.data?.pagoProveedorEliminado;
      if (pagoEliminado) {
        if (showNotifications) {
          handleSnackbar(
            `Pago eliminado del remito #${pagoEliminado.idRemito}`,
            'warning'
          );
        }
        
        if (onUpdate) {
          onUpdate('deleted', pagoEliminado);
        }
      }
    },
    onError: (error) => {
      console.error('Error en suscripción de pagos eliminados:', error);
    }
  });

  // Suscripción para archivos eliminados
  useSubscription(SUB_PAGO_PROVEEDOR_ARCHIVO_ELIMINADO, {
    onData: ({ data }) => {
      const pagoProveedor = data?.data?.pagoProveedorArchivoEliminado;
      if (pagoProveedor) {
        const proveedorName = getProveedorName(pagoProveedor.remito);
        
        if (showNotifications) {
          handleSnackbar(
            `Archivo de factura eliminado para ${proveedorName} (Remito #${pagoProveedor.idRemito})`,
            'info'
          );
        }
        
        if (onUpdate) {
          onUpdate('file_deleted', pagoProveedor);
        }
      }
    },
    onError: (error) => {
      console.error('Error en suscripción de archivos eliminados:', error);
    }
  });

  // Este componente no renderiza nada visible
  return null;
}

export default PagoProveedorSubscriptions;